import type { Ticket, SLAStatus, Priority } from '../types';
import { SLA_HOURS } from './constants';

export function fmt(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDT(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function slaStatus(ticket: Ticket): SLAStatus | null {
  const deadline = ticket.slaDueAt;
  if (!deadline || ['Completed','Rejected'].includes(ticket.status)) return null;

  const total     = SLA_HOURS[(ticket.priority as Priority)] ?? 24;
  const dueMs     = new Date(deadline).getTime();
  const nowMs     = Date.now();
  const elapsed   = Math.round((nowMs - (dueMs - total * 3_600_000)) / 3_600_000);
  const remaining = Math.round((dueMs - nowMs) / 3_600_000);
  const pct       = Math.min(100, Math.max(0, (elapsed / total) * 100));
  const isOverdue = remaining < 0;

  const status: SLAStatus['status'] =
    isOverdue    ? 'Overdue'  :
    pct >= 90    ? 'Critical' :
    pct >= 75    ? 'Warning'  : 'OK';

  const label = isOverdue ? 'OVERDUE' :
                pct >= 90 ? 'CRITICAL' :
                pct >= 75 ? 'WARNING'  : 'OK';

  return { label, isOverdue, pct: Math.round(pct), remaining, status, elapsed, total };
}

export function buildRefNum(seqNum: number, requesterName: string, firstItemCategory: string): string {
  const seq  = String(seqNum).padStart(5, '0');
  const name = (requesterName || 'User')
    .split(/[\s-]+/).slice(0, 2)
    .map(w => w.replace(/[^a-zA-Z]/g, ''))
    .join('') || 'USR';
  const item = (firstItemCategory || 'Request').replace(/[^a-zA-Z]/g, '').slice(0, 16) || 'Request';
  return `PSH-IT-FRM-${seq}-${name}-${item}`;
}

export function truncate(str: string | null | undefined, len = 40): string {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

export function classNames(...classes: (string | boolean | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// When a requester's line manager also holds the IT Manager role, the approval chain
// collapses to a single step (see backend planApproval) — but "Pending IT Manager" reads
// oddly to the requester, who only knows this person as their line manager. Show a
// friendlier, name-specific label from the requester's own point of view only; everyone
// else (IT staff/manager) still sees the standard status term, which is accurate for them.
export function requesterStatusLabel(ticket: Ticket, viewerIsRequester: boolean): string | null {
  // Status stays "Resolved" both before and after the requester confirms (confirming
  // doesn't close the ticket — SR Closed by the assignee is the sole final closure
  // action). Without this override, the badge would read "Awaiting Confirmation"
  // forever, even once already confirmed — this applies for every viewer (assignee/IT
  // manager included), not just the requester, since it's a factual state, not a
  // requester-specific reframing.
  if (ticket.status === 'Resolved' && ticket.requesterConfirmedAt) {
    return 'Confirmed — Awaiting SR Closure';
  }

  if (!viewerIsRequester) return null;

  const collapsed = !!ticket.lineManagerId && ticket.lineManagerId === ticket.itManagerId;
  if (!collapsed) return null;
  const managerName = ticket.itManager?.name || ticket.lineManager?.name || 'your manager';
  if (ticket.status === 'PendingITManager') {
    return `Waiting Approval — Line Manager (${managerName})`;
  }
  if (ticket.status === 'Approved' && !ticket.assignedToId) {
    return `Waiting Assigning — IT Manager (${managerName})`;
  }
  return null;
}

// Controllers that call res.json() directly return { message }. Joi validation failures
// return { message: 'Validation failed', errors: [...] }. Errors thrown from a service
// (next(e), caught by the global error handler in app.ts) return { error } instead — a
// different key. Without checking all three, thrown errors show a useless generic
// fallback instead of the actual reason (e.g. "Cannot close ticket in status: ...").
export function apiErrorMessage(e: any, fallback: string): string {
  const data = e?.response?.data;
  if (!data) return fallback;
  if (Array.isArray(data.errors) && data.errors.length) return data.errors.join('; ');
  const message = data.message || data.error || fallback;
  // The global error handler includes a reference id for genuine 500s (logged
  // server-side) — surface it so a report of "it failed" can actually be
  // correlated to the real error instead of being a dead end.
  return data.reference ? `${message} (ref: ${data.reference})` : message;
}
